import { z } from "zod";
import type { Args, Operation } from "../types";
import { scoped } from "../types";

// Copilot — the in-shell Chat agent (docs/copilot/PRD.md). These contracts cover
// conversation/message persistence (CRUD). The live token/tool-call stream rides
// the separate streaming seam in ./chat-stream.ts (createStreamRoute/useStream).

export type ChatMode = "operate" | "develop";
export type ChatRole = "user" | "assistant" | "tool" | "system";
export type ToolCallStatus =
  | "proposed"
  | "allowed"
  | "denied"
  | "ran"
  | "failed";

export interface ChatToolCall {
  uid: string;
  tool_name: string;
  input_json: string | null;
  result_json: string | null;
  status: ToolCallStatus;
  verdict_reason: string | null;
  created_at: string;
}

export interface ChatMessage {
  uid: string;
  role: ChatRole;
  content: string | null;
  worker_run_id: number | null;
  created_at: string;
  tool_calls: ChatToolCall[];
  // Per-turn usage (P4.4), present on assistant messages once a turn completes.
  input_tokens?: number | null;
  output_tokens?: number | null;
  cost_usd?: number | null;
}

export interface ChatConversation {
  uid: string;
  title: string | null;
  mode: ChatMode;
  branch: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

const toolCallSchema = z.object({
  uid: z.string(),
  tool_name: z.string(),
  input_json: z.string().nullable(),
  result_json: z.string().nullable(),
  status: z.enum(["proposed", "allowed", "denied", "ran", "failed"]),
  verdict_reason: z.string().nullable(),
  created_at: z.string(),
});

const messageSchema = z.object({
  uid: z.string(),
  role: z.enum(["user", "assistant", "tool", "system"]),
  content: z.string().nullable(),
  worker_run_id: z.number().nullable(),
  created_at: z.string(),
  tool_calls: z.array(toolCallSchema),
  input_tokens: z.number().nullable().optional(),
  output_tokens: z.number().nullable().optional(),
  cost_usd: z.number().nullable().optional(),
});

const conversationSchema = z.object({
  uid: z.string(),
  title: z.string().nullable(),
  mode: z.enum(["operate", "develop"]),
  branch: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  archived_at: z.string().nullable(),
});

// ── list ──────────────────────────────────────────────────────────────────
const listResponse = z.object({
  conversations: z.array(conversationSchema),
});
type ListResponse = z.infer<typeof listResponse>;

const listOp: Operation<void, void, void, ListResponse> = {
  method: "GET",
  path: () => "/api/chat/conversations",
  response: listResponse,
};

// ── create ────────────────────────────────────────────────────────────────
// `mode` is optional (defaulted server-side to "operate") — a zod `.default()`
// here would make the schema's input/output types diverge, which Operation's
// `body?: z.ZodType<TBody>` (input === output) rejects.
const createBody = z.object({
  mode: z.enum(["operate", "develop"]).optional(),
  title: z.string().optional(),
});
type CreateBody = z.infer<typeof createBody>;

const createResponse = z.object({ conversation: conversationSchema });
type CreateResponse = z.infer<typeof createResponse>;

const createOp: Operation<void, void, CreateBody, CreateResponse> = {
  method: "POST",
  path: () => "/api/chat/conversations",
  body: createBody,
  response: createResponse,
  invalidates: [listOp],
};

// ── get (with messages) ─────────────────────────────────────────────────────
const getParams = z.object({ id: z.string() });
type GetParams = z.infer<typeof getParams>;

const getResponse = z.object({
  conversation: conversationSchema,
  messages: z.array(messageSchema),
});
type GetResponse = z.infer<typeof getResponse>;

const getOp: Operation<GetParams, void, void, GetResponse> = {
  method: "GET",
  path: ({ id }) => `/api/chat/conversations/${id}`,
  params: getParams,
  response: getResponse,
};

// ── archive ─────────────────────────────────────────────────────────────────
const archiveResponse = z.object({ archived: z.boolean() });
type ArchiveResponse = z.infer<typeof archiveResponse>;

const archiveOp: Operation<GetParams, void, void, ArchiveResponse> = {
  method: "DELETE",
  path: ({ id }) => `/api/chat/conversations/${id}`,
  params: getParams,
  response: archiveResponse,
  invalidates: [listOp, scoped(getOp, (args: Args<{ id: string }, void, unknown>) => ({ id: args.params.id }))],
};

// ── rename ────────────────────────────────────────────────────────────────
const renameBody = z.object({ title: z.string() });
type RenameBody = z.infer<typeof renameBody>;

const renameResponse = z.object({ conversation: conversationSchema });
type RenameResponse = z.infer<typeof renameResponse>;

const renameOp: Operation<GetParams, void, RenameBody, RenameResponse> = {
  method: "PATCH",
  path: ({ id }) => `/api/chat/conversations/${id}`,
  params: getParams,
  body: renameBody,
  response: renameResponse,
  invalidates: [listOp, scoped(getOp, (args: Args<{ id: string }, void, unknown>) => ({ id: args.params.id }))],
};

// ── interactive permission resolve (P2.2) ──────────────────────────────────
const permissionBody = z.object({
  permission: z.string().min(1),
  decision: z.enum(["allow", "deny"]),
});
type PermissionBody = z.infer<typeof permissionBody>;

const permissionResponse = z.object({ resolved: z.boolean() });
type PermissionResponse = z.infer<typeof permissionResponse>;

const permissionOp: Operation<GetParams, void, PermissionBody, PermissionResponse> = {
  method: "POST",
  path: ({ id }) => `/api/chat/conversations/${id}/permission`,
  params: getParams,
  body: permissionBody,
  response: permissionResponse,
  invalidates: [scoped(getOp, (args: Args<{ id: string }, void, unknown>) => ({ id: args.params.id }))],
};

// ── develop checkpoints (P4.3 rollback) ─────────────────────────────────────
export interface ChatCheckpoint {
  uid: string;
  git_sha: string;
  label: string | null;
  is_last_good: number;
  created_at: string;
}
const checkpointSchema = z.object({
  uid: z.string(),
  git_sha: z.string(),
  label: z.string().nullable(),
  is_last_good: z.number(),
  created_at: z.string(),
});

const checkpointsResponse = z.object({ checkpoints: z.array(checkpointSchema) });
type CheckpointsResponse = z.infer<typeof checkpointsResponse>;

const checkpointsOp: Operation<GetParams, void, void, CheckpointsResponse> = {
  method: "GET",
  path: ({ id }) => `/api/chat/conversations/${id}/checkpoints`,
  params: getParams,
  response: checkpointsResponse,
};

const revertBody = z.object({ sha: z.string().min(7) });
type RevertBody = z.infer<typeof revertBody>;

const revertResponse = z.object({ ok: z.boolean(), note: z.string() });
type RevertResponse = z.infer<typeof revertResponse>;

const revertOp: Operation<GetParams, void, RevertBody, RevertResponse> = {
  method: "POST",
  path: ({ id }) => `/api/chat/conversations/${id}/checkpoints/revert`,
  params: getParams,
  body: revertBody,
  response: revertResponse,
  invalidates: [scoped(getOp, (args: Args<{ id: string }, void, unknown>) => ({ id: args.params.id })), scoped(checkpointsOp, (args: Args<{ id: string }, void, unknown>) => ({ id: args.params.id }))],
};

export const chat = {
  list: listOp,
  create: createOp,
  get: getOp,
  archive: archiveOp,
  rename: renameOp,
  resolvePermission: permissionOp,
  checkpoints: checkpointsOp,
  revert: revertOp,
};
